/*
 * Content system (cs-) behavior: client-side filter, scrollspy/TOC + reading
 * progress, accessible tabs. Dependency-free, loaded with defer.
 * Progressive enhancement: without JS every item, panel and heading is visible.
 * API (data attributes) is documented in designs/content/FOUNDATION.md.
 * Every cs- section includes this file, so it guards against running twice.
 */
(() => {
  if (window.csContentSystem) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tokens = (value) => String(value || '').toLowerCase().split(/\s+/).filter(Boolean);
  const isPlainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  /* ---------------------------------------------------------------------------
     Filter
     <div data-cs-filter-group id="cs-cases" data-cs-page-size="9" [data-cs-backfill]>
       pills: [data-cs-filter="industry"][data-value="retail"] (inside, or in a
              [data-cs-for~="cs-cases"] container; data-cs-for takes several
              space-separated ids, so one pill row can drive many groups)
       items: [data-cs-item][data-industry="retail healthcare"]
       [data-cs-count] [data-cs-empty] [data-cs-load-more [data-cs-load-all]] [data-cs-reset]
     data-cs-backfill: when fewer than page-size items match, the first
     non-matching items (DOM order) fill the gap, marked [data-cs-backfilled].
     --------------------------------------------------------------------------- */
  const groups = new Map();

  const scrollPillIntoView = (pill) => {
    const track = pill.closest('.cs-pills__list');
    if (!track || track.scrollWidth <= track.clientWidth) return;
    const left = pill.offsetLeft - track.offsetLeft;
    const right = left + pill.offsetWidth;
    const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    if (left - pad < track.scrollLeft) track.scrollLeft = left - pad;
    else if (right > track.scrollLeft + track.clientWidth) track.scrollLeft = right - track.clientWidth + pad;
  };

  const controlLabel = (el) => {
    if (el.dataset.label) return el.dataset.label;
    const clone = el.cloneNode(true);
    clone.querySelectorAll('.cs-pill__count').forEach((count) => count.remove());
    return clone.textContent.trim();
  };

  class FilterGroup {
    constructor(root) {
      this.root = root;
      this.pageSize = parseInt(root.dataset.csPageSize, 10) || 0;
      this.backfill = root.hasAttribute('data-cs-backfill');
      this.syncUrl = root.dataset.csUrl !== 'false';
      this.items = [...root.querySelectorAll('[data-cs-item]')];
      this.count = root.querySelector('[data-cs-count]');
      this.empty = root.querySelector('[data-cs-empty]');
      this.more = root.querySelector('[data-cs-load-more]');
      this.state = {};
      this.page = 1;

      const scopes = [root];
      if (root.id) scopes.push(...document.querySelectorAll(`[data-cs-for~="${CSS.escape(root.id)}"]`));
      this.controls = [...new Set(scopes.flatMap((scope) => [...scope.querySelectorAll('[data-cs-filter]')]))];
      this.keys = [...new Set(this.controls.map((el) => el.dataset.csFilter))];

      this.readInitialState();
      this.controls.forEach((el) => el.addEventListener('click', (event) => this.onControl(event, el)));
      root.querySelectorAll('[data-cs-reset]').forEach((el) => el.addEventListener('click', () => this.reset()));
      this.more?.addEventListener('click', () => this.loadMore());
      this.apply({ silent: true });
      this.controls.filter((el) => el.classList.contains('is-active')).forEach(scrollPillIntoView);
    }

    readInitialState() {
      const params = this.syncUrl ? new URLSearchParams(window.location.search) : null;
      this.keys.forEach((key) => {
        const fromUrl = params?.get(key);
        const active = this.controls.find((el) => el.dataset.csFilter === key && el.classList.contains('is-active'));
        this.state[key] = (fromUrl || active?.dataset.value || 'all').toLowerCase();
      });
    }

    onControl(event, el) {
      if (el.tagName === 'A' && !isPlainClick(event)) return;
      event.preventDefault();
      this.set(el.dataset.csFilter, el.dataset.value);
    }

    /** Public: set one dimension, e.g. group.set('industry', 'retail'). */
    set(key, value) {
      this.state[key] = String(value || 'all').toLowerCase();
      this.page = 1;
      this.apply();
    }

    /** Public: clear every dimension of this group. */
    reset() {
      this.keys.forEach((key) => (this.state[key] = 'all'));
      this.page = 1;
      this.apply();
    }

    isFiltered() {
      return this.keys.some((key) => this.state[key] && this.state[key] !== 'all');
    }

    matches(item) {
      return this.keys.every((key) => {
        const wanted = this.state[key];
        if (!wanted || wanted === 'all') return true;
        return tokens(item.getAttribute(`data-${key}`)).includes(wanted);
      });
    }

    apply({ silent = false, focusFrom = -1 } = {}) {
      const matched = this.items.filter((item) => this.matches(item));
      const limit = this.pageSize ? this.pageSize * this.page : matched.length;
      const backfill =
        this.backfill && this.pageSize && matched.length < this.pageSize
          ? this.items.filter((item) => !matched.includes(item)).slice(0, this.pageSize - matched.length)
          : [];

      this.items.forEach((item) => {
        item.hidden = true;
        item.removeAttribute('data-cs-backfilled');
      });
      matched.forEach((item, index) => (item.hidden = index >= limit));
      backfill.forEach((item) => {
        item.hidden = false;
        item.setAttribute('data-cs-backfilled', '');
      });

      const shown = Math.min(limit, matched.length);
      const visible = shown + backfill.length;
      if (this.count) {
        const template = this.count.dataset.csCountTemplate;
        this.count.textContent = template
          ? template.replace('{shown}', shown).replace('{total}', matched.length)
          : String(matched.length);
      }
      if (this.empty) this.empty.hidden = visible > 0;
      if (this.more) this.more.hidden = shown >= matched.length;
      this.root.dataset.csMatched = String(matched.length);
      this.root.toggleAttribute('data-cs-filtered', this.isFiltered());

      const labels = {};
      this.controls.forEach((el) => {
        const key = el.dataset.csFilter;
        const active = (this.state[key] || 'all') === String(el.dataset.value).toLowerCase();
        el.classList.toggle('is-active', active);
        if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', String(active));
        else if (active) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
        if (active) {
          labels[key] = controlLabel(el);
          if (!silent) scrollPillIntoView(el);
        }
      });

      if (!silent && this.syncUrl) this.writeUrl();
      if (focusFrom >= 0) matched[focusFrom]?.querySelector('a, button')?.focus();

      this.root.dispatchEvent(
        new CustomEvent('cs:filter', {
          bubbles: true,
          detail: {
            group: this.root,
            state: { ...this.state },
            labels,
            filtered: this.isFiltered(),
            total: matched.length,
            shown,
            backfilled: backfill.length,
          },
        })
      );
    }

    loadMore() {
      const firstNew = this.pageSize * this.page;
      // [data-cs-load-all] on the button reveals every match at once ("Show all 29 products")
      this.page = this.more?.hasAttribute('data-cs-load-all') ? Math.ceil(this.items.length / this.pageSize) : this.page + 1;
      this.apply({ silent: true, focusFrom: firstNew });
    }

    writeUrl() {
      const url = new URL(window.location.href);
      this.keys.forEach((key) => {
        if (this.state[key] && this.state[key] !== 'all') url.searchParams.set(key, this.state[key]);
        else url.searchParams.delete(key);
      });
      window.history.replaceState(window.history.state, '', url);
    }
  }

  /* A [data-cs-reset] outside any filter group clears every group on the page
     (e.g. the "Clear" link in a hero status line). */
  document.addEventListener('click', (event) => {
    const reset = event.target.closest?.('[data-cs-reset]');
    if (!reset || reset.closest('[data-cs-filter-group]')) return;
    event.preventDefault();
    groups.forEach((group) => group.root.isConnected && group.reset());
  });

  /* ---------------------------------------------------------------------------
     Scrollspy / TOC
     <nav data-cs-scrollspy [data-cs-toc-source="#article-body"]> links to #ids
     If the nav has no links, it is built from the source's h2s (ids added).
     Reading progress: [data-cs-progress][data-cs-progress-target="#article"]
     --------------------------------------------------------------------------- */
  const slugify = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section';

  const buildToc = (nav) => {
    const source = nav.dataset.csTocSource ? document.querySelector(nav.dataset.csTocSource) : null;
    const list = nav.querySelector('[data-cs-toc-list]') || nav.querySelector('ol, ul');
    if (!source || !list || list.querySelector('a')) return;
    const used = new Set();
    source.querySelectorAll(nav.dataset.csTocHeadings || 'h2').forEach((heading) => {
      if (!heading.id) {
        let id = slugify(heading.textContent);
        while (used.has(id) || document.getElementById(id)) id = `${id}-${used.size + 1}`;
        heading.id = id;
      }
      used.add(heading.id);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${heading.id}`;
      a.className = 'cs-toc__link';
      a.textContent = heading.textContent.trim();
      li.append(a);
      list.append(li);
    });
    if (!list.children.length) nav.hidden = true;
  };

  const initScrollspy = (nav) => {
    buildToc(nav);
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const targets = links
      .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
      .filter(Boolean);
    if (!targets.length || !('IntersectionObserver' in window)) return;

    const visible = new Set();
    const setActive = (id) =>
      links.forEach((link) => {
        const active = link.hash.slice(1) === id;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)));
        const current = targets.find((target) => visible.has(target));
        if (current) setActive(current.id);
      },
      { rootMargin: nav.dataset.csRootMargin || '-15% 0px -70% 0px' }
    );
    targets.forEach((target) => observer.observe(target));

    links.forEach((link) =>
      link.addEventListener('click', (event) => {
        const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!target || !isPlainClick(event)) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        window.history.replaceState(window.history.state, '', link.hash);
        setActive(target.id);
      })
    );
  };

  const progressBars = new Set();
  let progressFrame = 0;
  const updateProgress = () => {
    progressFrame = 0;
    progressBars.forEach((bar) => {
      if (!bar.isConnected) {
        progressBars.delete(bar);
        return;
      }
      const target = document.querySelector(bar.dataset.csProgressTarget || '') || document.documentElement;
      const rect = target.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      const value = distance > 0 ? Math.min(1, Math.max(0, -rect.top / distance)) : rect.top < 0 ? 1 : 0;
      bar.style.setProperty('--cs-progress', value.toFixed(4));
      bar.setAttribute('aria-valuenow', String(Math.round(value * 100)));
    });
  };
  const requestProgress = () => {
    if (!progressFrame) progressFrame = requestAnimationFrame(updateProgress);
  };

  /* ---------------------------------------------------------------------------
     Tabs
     <div data-cs-tabs>
       <div data-cs-tablist aria-label="..."> <button data-cs-tab aria-controls="p1">…
       <div data-cs-panel id="p1"> …
     No JS: tab list hidden (CSS), all panels shown.
     --------------------------------------------------------------------------- */
  const initTabs = (root) => {
    const list = root.querySelector('[data-cs-tablist]');
    const tabs = [...root.querySelectorAll('[data-cs-tab]')];
    if (!list || !tabs.length) return;
    list.setAttribute('role', 'tablist');

    const select = (tab, focus = false) => {
      tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tab.focus();
      root.dispatchEvent(new CustomEvent('cs:tab', { bubbles: true, detail: { tab } }));
    };

    tabs.forEach((tab, index) => {
      if (!tab.id) tab.id = `${tab.getAttribute('aria-controls')}-tab`;
      tab.setAttribute('role', 'tab');
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
      }
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        select(tabs[(keys[event.key] + tabs.length) % tabs.length], true);
      });
    });

    const initial = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    select(initial);
    root.classList.add('is-ready');
  };

  /* ---------------------------------------------------------------------------
     Init (page load + theme editor section re-renders)
     --------------------------------------------------------------------------- */
  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = 'ready';
    return true;
  };

  const init = (scope = document) => {
    groups.forEach((group, el) => !el.isConnected && groups.delete(el));
    scope.querySelectorAll('[data-cs-filter-group]').forEach((el) => {
      if (once(el, 'csFilterReady')) groups.set(el, new FilterGroup(el));
    });
    scope.querySelectorAll('[data-cs-scrollspy]').forEach((el) => once(el, 'csSpyReady') && initScrollspy(el));
    scope.querySelectorAll('[data-cs-tabs]').forEach((el) => once(el, 'csTabsReady') && initTabs(el));
    scope.querySelectorAll('[data-cs-progress]').forEach((el) => {
      if (!once(el, 'csProgressReady')) return;
      el.setAttribute('role', 'progressbar');
      el.setAttribute('aria-valuemin', '0');
      el.setAttribute('aria-valuemax', '100');
      if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', 'Reading progress');
      progressBars.add(el);
    });
    if (progressBars.size) requestProgress();
  };

  window.addEventListener('scroll', requestProgress, { passive: true });
  window.addEventListener('resize', requestProgress, { passive: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.csContentSystem = { init, getFilter: (el) => groups.get(el), groups };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
